import {
  buildResponsibilityPatch,
  buildTaskCompletionPatch,
} from './nodeResponsibility';

const relationshipKey = (parentId, childId) => `${parentId}:${childId}`;

export function stripTransientNodeFields(node = {}) {
  const sharedNode = { ...node };
  delete sharedNode.autoFocus;
  delete sharedNode.animate;
  return sharedNode;
}

export function applyCreateNodeTransaction({ doc, yNodes, yRels, undoManager, command }) {
  const node = command?.node;
  const relationship = command?.relationship;

  if (!node?.id || !relationship?.parentId || relationship.childId !== node.id) {
    throw new Error('Invalid create-node command');
  }
  if (!yNodes.has(relationship.parentId)) {
    throw new Error(`Parent node ${relationship.parentId} does not exist`);
  }
  if (yNodes.has(node.id)) {
    throw new Error(`Node ${node.id} already exists`);
  }

  const { id, ...nodeData } = stripTransientNodeFields(node);
  const key = relationshipKey(relationship.parentId, relationship.childId);

  // Keep this user-visible command isolated from edits immediately before
  // or after it, while storing node and edge in one CRDT transaction.
  undoManager?.stopCapturing();
  doc.transact(() => {
    yNodes.set(id, nodeData);
    yRels.set(key, relationship);
  });
  undoManager?.stopCapturing();

  return id;
}

// Compatibility name for callers introduced in the first command slice.
export const applyCreateChildTransaction = applyCreateNodeTransaction;

export function applyCreateInboxIdeaTransaction({ doc, yNodes, undoManager, command }) {
  const node = command?.node;
  if (command?.type !== 'create-inbox-idea' || !node?.id || node.inbox?.state !== 'inbox') {
    throw new Error('Invalid create-inbox-idea command');
  }
  if (yNodes.has(node.id)) throw new Error(`Node ${node.id} already exists`);
  const { id, ...nodeData } = stripTransientNodeFields(node);
  undoManager?.stopCapturing();
  doc.transact(() => yNodes.set(id, nodeData));
  undoManager?.stopCapturing();
  return id;
}

export function applyMoveBranchToInboxTransaction({ doc, yNodes, yRels, undoManager, command }) {
  const removed = command?.removeRelationship;
  if (
    command?.type !== 'move-branch-to-inbox'
    || !command.nodeId
    || !removed?.parentId
    || removed.childId !== command.nodeId
    || command.inbox?.state !== 'inbox'
  ) {
    throw new Error('Invalid move-branch-to-inbox command');
  }
  const node = yNodes.get(command.nodeId);
  if (!node) throw new Error(`Node ${command.nodeId} does not exist`);
  if (node.isRoot) throw new Error('The main idea cannot be moved to the inbox');
  const key = relationshipKey(removed.parentId, removed.childId);
  const currentParents = [...yRels.entries()].filter(([, relationship]) => (
    relationship?.childId === command.nodeId
  ));
  if (currentParents.length !== 1 || currentParents[0][0] !== key) {
    throw new Error('The idea moved before it could be added to the inbox');
  }

  undoManager?.stopCapturing();
  doc.transact(() => {
    yRels.delete(key);
    yNodes.set(command.nodeId, stripTransientNodeFields({ ...node, inbox: command.inbox }));
  });
  undoManager?.stopCapturing();
  return command.nodeId;
}

export function applyAttachInboxBranchTransaction({
  doc,
  yNodes,
  yRels,
  undoManager,
  command,
  lastEditedBy = null,
}) {
  const relationship = command?.relationship;
  if (
    command?.type !== 'attach-inbox-branch'
    || !command.nodeId
    || !relationship?.parentId
    || relationship.childId !== command.nodeId
    || !Array.isArray(command.positions)
  ) {
    throw new Error('Invalid attach-inbox-branch command');
  }
  const rootNode = yNodes.get(command.nodeId);
  if (!rootNode || rootNode.inbox?.state !== 'inbox') {
    throw new Error('That idea is no longer in the inbox');
  }
  if (!yNodes.has(relationship.parentId)) throw new Error('The destination idea no longer exists');
  for (const [, existing] of yRels.entries()) {
    if (existing?.childId === command.nodeId) {
      throw new Error('That inbox idea has already been placed');
    }
  }
  const childrenByParentId = new Map();
  const parentByChildId = new Map();
  for (const [, existing] of yRels.entries()) {
    if (!existing?.parentId || !existing?.childId) continue;
    const children = childrenByParentId.get(existing.parentId) || [];
    children.push(existing.childId);
    childrenByParentId.set(existing.parentId, children);
    parentByChildId.set(existing.childId, existing.parentId);
  }
  const subtreeIds = new Set();
  const pending = [command.nodeId];
  for (let index = 0; index < pending.length; index += 1) {
    const nodeId = pending[index];
    if (!nodeId || subtreeIds.has(nodeId)) continue;
    subtreeIds.add(nodeId);
    pending.push(...(childrenByParentId.get(nodeId) || []));
  }
  if (subtreeIds.has(relationship.parentId)) {
    throw new Error('Placing that branch would create a cycle');
  }
  const visitedAncestors = new Set();
  let ancestorId = relationship.parentId;
  while (ancestorId && !visitedAncestors.has(ancestorId)) {
    visitedAncestors.add(ancestorId);
    if (yNodes.get(ancestorId)?.inbox?.state === 'inbox') {
      throw new Error('The destination moved into the inbox');
    }
    ancestorId = parentByChildId.get(ancestorId) || null;
  }
  const updates = new Map(command.positions.map((entry) => [entry.id, entry]));
  updates.forEach((entry, nodeId) => {
    if (!yNodes.has(nodeId)) throw new Error(`Node ${nodeId} does not exist`);
    if (!Number.isFinite(entry.position?.x) || !Number.isFinite(entry.position?.y)) {
      throw new Error(`Node ${nodeId} requires a finite position`);
    }
  });

  undoManager?.stopCapturing();
  doc.transact(() => {
    updates.forEach((entry, nodeId) => {
      const current = yNodes.get(nodeId);
      const next = {
        ...current,
        position: entry.position,
        ...(entry.layout ? { layout: entry.layout } : {}),
        ...(lastEditedBy ? { lastEditedBy } : {}),
      };
      if (nodeId === command.nodeId) delete next.inbox;
      yNodes.set(nodeId, stripTransientNodeFields(next));
    });
    yRels.set(
      relationshipKey(relationship.parentId, relationship.childId),
      relationship
    );
  });
  undoManager?.stopCapturing();
  return command.nodeId;
}

export function applyResponsibilityTransaction({
  doc,
  yNodes,
  undoManager,
  nodeId,
  assignee,
  actorUserId,
}) {
  if (!nodeId || !actorUserId) throw new Error('A task idea and assigning user are required');

  let responsiblePrincipal = null;
  undoManager?.stopCapturing();
  try {
    doc.transact(() => {
      const current = yNodes.get(nodeId);
      if (!current) throw new Error('The task no longer exists.');
      if (!current.isTask) throw new Error('This idea is no longer a task.');
      const patch = buildResponsibilityPatch({
        node: { id: nodeId, ...current },
        assignee,
        actorUserId,
      });
      responsiblePrincipal = patch.responsiblePrincipal;
      yNodes.set(nodeId, stripTransientNodeFields({ ...current, ...patch }));
    });
  } finally {
    undoManager?.stopCapturing();
  }
  return responsiblePrincipal;
}

export function applyTaskCompletionTransaction({
  doc,
  yNodes,
  undoManager,
  nodeId,
  done,
  actorUserId,
}) {
  if (!nodeId || !actorUserId) throw new Error('A task idea and completing user are required');

  let taskDone = false;
  undoManager?.stopCapturing();
  try {
    doc.transact(() => {
      const current = yNodes.get(nodeId);
      if (!current) throw new Error('The task no longer exists.');
      if (!current.isTask) throw new Error('This idea is no longer a task.');
      const creatorUserId = current.userId || null;
      const assigneeUserId = current.responsiblePrincipal?.userId || null;
      if (
        (creatorUserId || assigneeUserId)
        && creatorUserId !== actorUserId
        && assigneeUserId !== actorUserId
      ) {
        throw new Error('Only the assignee or idea creator can complete this task.');
      }
      const patch = buildTaskCompletionPatch({
        node: { id: nodeId, ...current },
        done,
        actorUserId,
      });
      taskDone = patch.taskDone;
      yNodes.set(nodeId, stripTransientNodeFields({ ...current, ...patch }));
    });
  } finally {
    undoManager?.stopCapturing();
  }
  return taskDone;
}

function collectYjsSubtreeIds(nodeId, yRels) {
  const relationships = [...yRels.values()];
  const childrenByParent = new Map();
  relationships.forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });

  const nodeIds = [];
  const visited = new Set();
  const pending = [nodeId];
  let pendingIndex = 0;
  while (pendingIndex < pending.length) {
    const current = pending[pendingIndex];
    pendingIndex += 1;
    if (!current || visited.has(current)) continue;
    visited.add(current);
    nodeIds.push(current);
    pending.push(...(childrenByParent.get(current) || []));
  }
  return nodeIds;
}

export function applyDeleteSubtreeTransaction({ doc, yNodes, yRels, undoManager, command }) {
  if (command?.type !== 'delete-subtree' || !command.nodeId || !Array.isArray(command.nodeIds)) {
    throw new Error('Invalid delete-subtree command');
  }
  if (!command.nodeIds.length || new Set(command.nodeIds).size !== command.nodeIds.length) {
    throw new Error('Delete command requires unique branch node ids');
  }

  const currentNodeIds = collectYjsSubtreeIds(command.nodeId, yRels);
  const expectedIds = new Set(command.nodeIds);
  if (
    currentNodeIds.length !== expectedIds.size
    || currentNodeIds.some((nodeId) => !expectedIds.has(nodeId))
  ) {
    throw new Error('The branch changed before it could be deleted; try again');
  }
  const currentParentIds = [...yRels.values()]
    .filter((relationship) => relationship?.childId === command.nodeId)
    .map((relationship) => relationship.parentId);
  if (
    command.parentId
    && (currentParentIds.length !== 1 || currentParentIds[0] !== command.parentId)
  ) {
    throw new Error('The branch moved before it could be deleted; try again');
  }
  command.nodeIds.forEach((nodeId) => {
    if (!yNodes.has(nodeId)) throw new Error(`Node ${nodeId} does not exist`);
  });

  const deletingIds = new Set(command.nodeIds);
  undoManager?.stopCapturing();
  doc.transact(() => {
    command.nodeIds.forEach((nodeId) => yNodes.delete(nodeId));
    for (const [key, relationship] of yRels.entries()) {
      if (deletingIds.has(relationship?.parentId) || deletingIds.has(relationship?.childId)) {
        yRels.delete(key);
      }
    }
  });
  undoManager?.stopCapturing();
  return command.nextSelectedId || null;
}

function sameIdSet(left, right) {
  if (left.length !== right.length) return false;
  const leftIds = new Set(left);
  const rightIds = new Set(right);
  return leftIds.size === left.length
    && rightIds.size === right.length
    && left.every((id) => rightIds.has(id));
}

function canonicalValue(value) {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => ({ ...result, [key]: canonicalValue(value[key]) }), {});
  }
  return value ?? null;
}

function canonicalJson(value, fallback) {
  return JSON.stringify(canonicalValue(value ?? fallback));
}

export function collaborativeContentSnapshot(id, node = {}) {
  return {
    id,
    text: String(node.text || ''),
    linkUrl: node.linkUrl || null,
    linkTitle: node.linkMeta?.title || null,
    userId: node.userId || null,
    tags: canonicalJson(node.tags, []),
    inboxState: node.inbox?.state || null,
    inboxAddedAt: node.inbox?.addedAt || null,
    inboxPreviousParentId: node.inbox?.previousParentId || null,
    attachmentKind: node.attachment?.kind || null,
    isRoot: Boolean(node.isRoot),
    layoutOrder: Number.isFinite(node.layout?.order) ? node.layout.order : null,
    positionX: Number.isFinite(node.position?.x) ? node.position.x : 0,
    positionY: Number.isFinite(node.position?.y) ? node.position.y : 0,
    isTask: Boolean(node.isTask),
    taskDone: Boolean(node.taskDone),
    responsiblePrincipal: canonicalJson(node.responsiblePrincipal, null),
    responsibilityAssignedAt: node.responsibilityAssignedAt || null,
    responsibilityAssignedByUserId: node.responsibilityAssignedByUserId || null,
    responsibilityHistory: canonicalJson(node.responsibilityHistory, []),
    taskCompletedAt: node.taskCompletedAt || null,
    taskCompletedByUserId: node.taskCompletedByUserId || null,
    taskCompletionHistory: canonicalJson(node.taskCompletionHistory, []),
  };
}

export function applyReplaceBranchWithMarkdownTransaction({
  doc,
  yNodes,
  yRels,
  undoManager,
  command,
}) {
  if (
    command?.type !== 'replace-branch-with-markdown'
    || !command.nodeId
    || !Array.isArray(command.eligibleIds)
    || !Array.isArray(command.expectedEligibleNodes)
    || !Array.isArray(command.branchNodeIds)
    || !Array.isArray(command.branchRelationships)
    || !Array.isArray(command.removedNodeIds)
    || !Array.isArray(command.reparentedExclusions)
    || !command.nodePatch?.attachment?.assetId
  ) {
    throw new Error('Invalid branch-to-Markdown command');
  }
  if (!command.eligibleIds.includes(command.nodeId)) {
    throw new Error('The selected idea must be included in the Markdown branch');
  }
  if (!sameIdSet(
    command.expectedEligibleNodes.map((node) => node.id),
    command.eligibleIds
  )) {
    throw new Error('The replacement command requires a snapshot of every included idea');
  }
  if (!yNodes.has(command.nodeId)) throw new Error(`Node ${command.nodeId} does not exist`);
  if (!sameIdSet(command.branchNodeIds, collectYjsSubtreeIds(command.nodeId, yRels))) {
    throw new Error('The branch changed before it could be converted; try again');
  }

  const branchIds = new Set(command.branchNodeIds);
  const expectedRelationshipKeys = command.branchRelationships
    .map((relationship) => relationshipKey(relationship.parentId, relationship.childId));
  const currentRelationshipKeys = [...yRels.values()]
    .filter((relationship) => (
      branchIds.has(relationship?.parentId) || branchIds.has(relationship?.childId)
    ))
    .map((relationship) => relationshipKey(relationship.parentId, relationship.childId));
  if (!sameIdSet(expectedRelationshipKeys, currentRelationshipKeys)) {
    throw new Error('The branch structure changed before it could be converted; try again');
  }

  const contentChanged = command.expectedEligibleNodes.some((expected) => (
    JSON.stringify(collaborativeContentSnapshot(expected.id, yNodes.get(expected.id)))
      !== JSON.stringify(expected)
  ));
  if (contentChanged) {
    throw new Error('An idea changed before the branch could be converted; review it again');
  }

  const removedIds = new Set(command.removedNodeIds);
  if (
    removedIds.has(command.nodeId)
    || !sameIdSet(
      command.removedNodeIds,
      command.eligibleIds.filter((nodeId) => nodeId !== command.nodeId)
    )
  ) {
    throw new Error('The replacement command would remove an ineligible idea');
  }
  command.removedNodeIds.forEach((nodeId) => {
    if (!yNodes.has(nodeId)) throw new Error(`Node ${nodeId} does not exist`);
  });
  command.reparentedExclusions.forEach((entry) => {
    const oldKey = relationshipKey(entry.previousParentId, entry.childId);
    if (
      entry.parentId !== command.nodeId
      || !removedIds.has(entry.previousParentId)
      || !yNodes.has(entry.childId)
      || !yRels.has(oldKey)
    ) {
      throw new Error('An excluded attachment branch changed before conversion; try again');
    }
  });
  const requiredReparents = [...yRels.values()]
    .filter((relationship) => (
      removedIds.has(relationship?.parentId) && !removedIds.has(relationship?.childId)
    ))
    .map((relationship) => `${relationship.parentId}:${relationship.childId}`);
  const plannedReparents = command.reparentedExclusions
    .map((entry) => `${entry.previousParentId}:${entry.childId}`);
  if (!sameIdSet(requiredReparents, plannedReparents)) {
    throw new Error('An excluded attachment branch is not safely reparented');
  }

  undoManager?.stopCapturing();
  doc.transact(() => {
    const selected = yNodes.get(command.nodeId) || {};
    yNodes.set(command.nodeId, stripTransientNodeFields({
      ...selected,
      ...command.nodePatch,
    }));
    command.removedNodeIds.forEach((nodeId) => yNodes.delete(nodeId));
    for (const [key, relationship] of yRels.entries()) {
      if (removedIds.has(relationship?.parentId) || removedIds.has(relationship?.childId)) {
        yRels.delete(key);
      }
    }
    command.reparentedExclusions.forEach((entry) => {
      yRels.set(relationshipKey(command.nodeId, entry.childId), {
        parentId: command.nodeId,
        childId: entry.childId,
      });
    });
  });
  undoManager?.stopCapturing();
  return command.nodeId;
}

function validatePositionCommand({ yNodes, command, expectedType = null }) {
  if (expectedType && command?.type !== expectedType) {
    throw new Error(`Invalid ${expectedType} command`);
  }
  if (!Array.isArray(command?.positions) || command.positions.length === 0) {
    throw new Error('Position command requires at least one node');
  }

  command.positions.forEach((entry) => {
    if (!entry?.id || !yNodes.has(entry.id)) {
      throw new Error(`Node ${entry?.id || ''} does not exist`);
    }
    if (!Number.isFinite(entry.position?.x) || !Number.isFinite(entry.position?.y)) {
      throw new Error(`Node ${entry.id} requires a finite position`);
    }
  });
}

function setCommandPositions(yNodes, command, lastEditedBy) {
  command.positions.forEach((entry) => {
    const current = yNodes.get(entry.id) || {};
    yNodes.set(entry.id, {
      ...current,
      position: entry.position,
      ...(entry.layout ? { layout: entry.layout } : {}),
      ...(lastEditedBy ? { lastEditedBy } : {}),
    });
  });
}

export function applyMoveNodesTransaction({
  doc,
  yNodes,
  undoManager,
  command,
  lastEditedBy = null,
  isolate = false,
}) {
  validatePositionCommand({ yNodes, command });
  if (isolate) undoManager?.stopCapturing();
  doc.transact(() => setCommandPositions(yNodes, command, lastEditedBy));
  if (isolate) undoManager?.stopCapturing();
  return command.nodeId;
}

export function beginUndoCaptureInteraction(undoManager, interactionRef) {
  if (!undoManager || !interactionRef || interactionRef.current) return false;
  undoManager.stopCapturing?.();
  interactionRef.current = {
    undoManager,
    captureTimeout: undoManager.captureTimeout,
  };
  // Direct-drag position transactions and the final release-settling
  // transaction form one user command even when the gesture lasts longer
  // than Yjs's default capture timeout.
  undoManager.captureTimeout = Number.MAX_SAFE_INTEGER;
  return true;
}

export function endUndoCaptureInteraction(interactionRef) {
  const interaction = interactionRef?.current;
  if (!interaction) return false;
  interaction.undoManager.stopCapturing?.();
  interaction.undoManager.captureTimeout = interaction.captureTimeout;
  interactionRef.current = null;
  return true;
}

export function applyReparentTransaction({
  doc,
  yNodes,
  yRels,
  undoManager,
  command,
  lastEditedBy = null,
  isolate = true,
}) {
  validatePositionCommand({ yNodes, command, expectedType: 'reparent-subtree' });
  const removed = command.removeRelationship;
  const added = command.relationship;
  if (!removed?.parentId || removed.childId !== command.nodeId) {
    throw new Error('Invalid relationship removal');
  }
  if (!added?.parentId || added.childId !== command.nodeId) {
    throw new Error('Invalid replacement relationship');
  }
  if (!yNodes.has(added.parentId)) {
    throw new Error(`Parent node ${added.parentId} does not exist`);
  }

  const oldKey = relationshipKey(removed.parentId, removed.childId);
  const newKey = relationshipKey(added.parentId, added.childId);
  if (!yRels.has(oldKey)) throw new Error('Existing parent relationship does not exist');

  if (isolate) undoManager?.stopCapturing();
  doc.transact(() => {
    setCommandPositions(yNodes, command, lastEditedBy);
    yRels.delete(oldKey);
    yRels.set(newKey, added);
  });
  if (isolate) undoManager?.stopCapturing();
  return command.nodeId;
}
